/**
 * ConnectionStatus Component
 * 
 * Supabase 연결 상태를 모니터링하고 사용자에게 시각적 피드백을 제공합니다.
 * 멀티탭 환경에서 연결 문제 발생 시 자동 복구를 시도합니다.
 */

import React, { useEffect, useState } from 'react';
import { AlertCircle, CheckCircle, RefreshCw, Wifi, WifiOff } from 'lucide-react';
import { useConnectionStatus } from '@/hooks/useSupabaseQuery';
import { useQueryClient } from '@tanstack/react-query';

interface ConnectionStatusProps {
  /** 연결 상태 표시 위치 */
  position?: 'top' | 'bottom';
  /** 자동 숨기기 시간 (ms) - 0이면 자동 숨기기 안함 */
  autoHideDelay?: number;
  /** 개발 모드에서만 표시 */
  showInDevelopmentOnly?: boolean;
}

export const ConnectionStatus: React.FC<ConnectionStatusProps> = ({
  position = 'top',
  autoHideDelay = 5000,
  showInDevelopmentOnly = false
}) => {
  const { data: connectionStatus, isLoading, error, refetch } = useConnectionStatus();
  const queryClient = useQueryClient();
  const [isVisible, setIsVisible] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);

  // 개발 환경 체크
  const isDevelopment = process.env.NODE_ENV === 'development';
  const shouldShow = showInDevelopmentOnly ? isDevelopment : true;

  useEffect(() => {
    // 연결 문제가 있을 때만 표시
    if (connectionStatus && !connectionStatus.connected) {
      setIsVisible(true);
      
      // 자동 숨기기 설정이 있으면 타이머 설정
      if (autoHideDelay > 0) {
        const timer = setTimeout(() => {
          setIsVisible(false);
        }, autoHideDelay);
        
        return () => clearTimeout(timer);
      }
    } else if (connectionStatus?.connected) {
      // 연결이 복구되면 잠시 성공 메시지 표시 후 숨기기
      setIsVisible(true);
      const timer = setTimeout(() => {
        setIsVisible(false);
      }, 2000);
      
      return () => clearTimeout(timer);
    }
  }, [connectionStatus, autoHideDelay]);

  const handleRetry = async () => {
    setIsRetrying(true);
    try {
      // 연결 상태 재확인
      await refetch();
      
      // 모든 stale 쿼리 무효화하여 새로운 데이터 가져오기
      await queryClient.invalidateQueries({
        type: 'active',
        stale: true
      });
      
      console.log('🔄 Manual connection recovery completed');
    } catch (error) {
      console.error('❌ Manual connection recovery failed:', error);
    } finally {
      setIsRetrying(false);
    }
  };

  // 조건에 맞지 않으면 렌더링하지 않음
  if (!shouldShow || !isVisible || isLoading) {
    return null;
  }

  const isConnected = connectionStatus?.connected ?? false;
  const errorDetails = connectionStatus?.details;

  // 연결 상태에 따른 스타일과 메시지
  const getStatusConfig = () => {
    if (isConnected) {
      return {
        bgColor: 'bg-green-50 border-green-200',
        textColor: 'text-green-800',
        icon: CheckCircle,
        iconColor: 'text-green-600',
        message: '연결이 복구되었습니다',
        showRetry: false
      };
    }

    return {
      bgColor: 'bg-amber-50 border-amber-200',
      textColor: 'text-amber-800',
      icon: WifiOff,
      iconColor: 'text-amber-600',
      message: '연결 상태를 확인하는 중...',
      showRetry: true
    };
  };

  const config = getStatusConfig();
  const StatusIcon = config.icon;

  const positionClasses = position === 'top' 
    ? 'top-4 left-1/2 transform -translate-x-1/2'
    : 'bottom-4 left-1/2 transform -translate-x-1/2';

  return (
    <div 
      className={`fixed ${positionClasses} z-50 max-w-md mx-auto transition-all duration-300`}
      role="alert"
      aria-live="polite"
    >
      <div className={`${config.bgColor} border rounded-lg p-3 shadow-lg backdrop-blur-sm`}>
        <div className="flex items-center justify-between space-x-3">
          <div className="flex items-center space-x-2">
            <StatusIcon className={`h-5 w-5 ${config.iconColor}`} />
            <div>
              <p className={`text-sm font-medium ${config.textColor}`}>
                {config.message}
              </p>
              {errorDetails && (
                <p className={`text-xs ${config.textColor} opacity-75 mt-1`}>
                  {errorDetails === 'session_error' && '인증 세션 문제'}
                  {errorDetails === 'database_error' && '데이터베이스 연결 문제'}
                  {errorDetails === 'connection_check_failed' && '연결 확인 실패'}
                  {errorDetails === 'database_test_failed' && '데이터베이스 테스트 실패'}
                </p>
              )}
            </div>
          </div>
          
          {config.showRetry && (
            <button
              onClick={handleRetry}
              disabled={isRetrying}
              className={`flex items-center space-x-1 px-2 py-1 rounded text-xs font-medium transition-colors ${config.textColor} hover:bg-white hover:bg-opacity-50 disabled:opacity-50`}
              aria-label="연결 재시도"
            >
              <RefreshCw className={`h-3 w-3 ${isRetrying ? 'animate-spin' : ''}`} />
              <span>{isRetrying ? '재연결 중...' : '재시도'}</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * 개발 환경에서만 표시되는 연결 상태 컴포넌트
 */
export const DevelopmentConnectionStatus: React.FC = () => (
  <ConnectionStatus 
    showInDevelopmentOnly={true}
    position="bottom"
    autoHideDelay={0}
  />
);

export default ConnectionStatus;